import type { Message } from '../../../../contexts/ConversationContext';
import type { ChatDagNodeType } from '../types';
import { chatDagWarn } from './warnTelemetry';

/**
 * Classifier registry — Array<{kind, match, priority}>.
 *
 * Adding a new propose-class kind (e.g., merge_marker for Wave 2
 * propose_merge) is a one-row append. No switch edit. The classifier
 * sorts by priority (higher wins on tie) and returns the first match.
 *
 * author_kind dual-read: top-level wins on conflict (canonical writer
 * is backend session_manager.append_peer_message). When top-level +
 * metadata.author_kind both present AND differ, warn (rate-limited
 * per msg.id via warnTelemetry.chatDagWarn).
 */
type ClassifierRule = {
  kind: ChatDagNodeType;
  match: (msg: Message) => boolean;
  priority?: number;
};

const CLASSIFIERS: ClassifierRule[] = [
  {
    kind: 'spine_root_marker',
    match: (m) => (m.metadata as any)?.is_spine_root === true,
    priority: 10,
  },
  {
    kind: 'peer_response',
    match: (m) => {
      const top = (m as any).author_kind;
      const meta = (m.metadata as any)?.author_kind;
      if (top != null && meta != null && top !== meta) {
        chatDagWarn('classify_drift', m.id, { msgId: m.id, top, meta });
      }
      return (top ?? meta) === 'peer';
    },
    priority: 5,
  },
  {
    // Squad fold-back SP-1 — the synth turn is author_kind:'peer' but carries
    // metadata.kind==='squad_synth'; outrank peer_response (5).
    kind: 'squad_merge',
    match: (m) => (m.metadata as any)?.kind === 'squad_synth',
    priority: 8,
  },
  {
    // ChatDAGRAGDAG C2.2 merge + C2.3 switchback — the host-authored fold turn is
    // author_kind:'primary' (NOT 'peer') but carries metadata.kind in the fold set
    // (council_merge | council_switchback). One-row append, outranks peer_response
    // (5). A positive ships-test guards this registration (C2.1 once shipped a
    // dead-registered classifier).
    kind: 'merge_node',
    match: (m) => {
      const k = (m.metadata as any)?.kind;
      return k === 'council_merge' || k === 'council_switchback';
    },
    priority: 9,
  },
];

// Sort once at module init — registry is static.
const SORTED_CLASSIFIERS = [...CLASSIFIERS].sort(
  (a, b) => (b.priority ?? 0) - (a.priority ?? 0),
);

export function classifyAssistant(msg: Message): ChatDagNodeType {
  for (const rule of SORTED_CLASSIFIERS) {
    if (rule.match(msg)) return rule.kind;
  }
  return 'turn_marker';
}
